/**
 * De feedbackknop: een opmerking van een meelezer, mét de context van het cijfer.
 *
 * `context` is het deel dat ertoe doet. "Dit klopt niet" is over drie weken
 * onbruikbaar; "dit klopt niet" met verslagjaar, run-id en object erbij is een
 * bevinding die terug te leiden is tot de vastgelegde berekening.
 */
import { api } from "./client";

export type FeedbackContext = Record<string, string | number | boolean | null>;

export type FeedbackRequest = {
  message: string;
  screen: string;
  url: string;
  author_name?: string;
  context: FeedbackContext;
};

/**
 * De drie toestanden uit migratie 0019. Bewust geen vierde ("afgewezen"): een
 * opmerking die niet wordt opgepakt verdient een antwoord aan de inzender, niet
 * een statuslabel dat het gesprek vervangt.
 */
export type FeedbackStatus = "open" | "in_progress" | "handled";

export const FEEDBACK_STATUSES: FeedbackStatus[] = ["open", "in_progress", "handled"];

/** Wat er in de bediening staat. De API kent alleen de codes. */
export const FEEDBACK_STATUS_LABELS: Record<FeedbackStatus, string> = {
  open: "Nog oppakken",
  in_progress: "Mee bezig",
  handled: "Afgerond",
};

export type FeedbackItem = {
  id: string;
  /** Vast volgnummer per corporatie; verandert nooit. */
  number: number;
  /** Het ticketnummer zoals het in een mail of PR staat: "FB-001". */
  ticket: string;
  author: string;
  author_name: string | null;
  screen: string;
  url: string;
  message: string;
  context: FeedbackContext;
  status: FeedbackStatus;
  /** Wie de opmerking afrondde; leeg zodra hij weer openstaat. */
  handled_by: string | null;
  handled_at: string | null;
  created_at: string;
  /** Aantal reacties (updates en aanvullingen), zonder de statusregels. */
  comment_count: number;
  /** De laatste regel in de tijdlijn, of de binnenkomst als die leeg is. */
  last_activity_at: string;
};

/** Eén regel in de tijdlijn van een ticket. */
export type FeedbackTimelineEntry = {
  id: string;
  /** `comment`: een update of aanvulling · `status`: een statuswijziging. */
  kind: "comment" | "status";
  message: string;
  author: string;
  author_name: string | null;
  /** Geschreven door het ontwikkelteam (schrijfrol) of door de melder. */
  from_team: boolean;
  created_at: string;
};

export type FeedbackTicket = FeedbackItem & {
  timeline: FeedbackTimelineEntry[];
  attachments: FeedbackAttachment[];
  may_manage: boolean;
  may_comment: boolean;
};

/** Eén ticket met zijn tijdlijn. `ticket` mag "FB-012" of "12" zijn. */
export const fetchFeedbackTicket = async (ticket: string): Promise<FeedbackTicket> => {
  const { data } = await api.get<FeedbackTicket>(`/feedback/${encodeURIComponent(ticket)}`);
  return data;
};

export const postFeedbackComment = async (
  ticket: string, message: string, authorName?: string,
): Promise<FeedbackTicket> => {
  const { data } = await api.post<FeedbackTicket>(`/feedback/${encodeURIComponent(ticket)}/comments`, {
    message, author_name: authorName,
  });
  return data;
};

export type FeedbackList = {
  /** Aantal rijen in dit venster — volgt het filter. */
  count: number;
  /** Aantal per status over alles wat deze gebruiker mag zien; volgt het filter níet. */
  totals: Record<FeedbackStatus, number>;
  /** Mag deze sessie de status verzetten? Bepaalt of de kolom bediening of weergave is. */
  may_manage: boolean;
  /** `all` voor een schrijfrol, `own` voor een meelezer die alleen zichzelf ziet. */
  scope: "all" | "own";
  items: FeedbackItem[];
};

export const postFeedback = async (payload: FeedbackRequest): Promise<FeedbackItem> => {
  const { data } = await api.post<FeedbackItem>("/feedback", payload);
  return data;
};

/** Alle opmerkingen, nieuwste eerst. Vraagt een schrijfrol; een meelezer krijgt 403. */
export const fetchFeedback = async (status?: FeedbackStatus): Promise<FeedbackList> => {
  const { data } = await api.get<FeedbackList>("/feedback", {
    params: status ? { status } : undefined,
  });
  return data;
};

export const setFeedbackStatus = async (
  id: string,
  status: FeedbackStatus,
): Promise<FeedbackItem> => {
  const { data } = await api.post<FeedbackItem>(`/feedback/${id}/status`, { status });
  return data;
};

/** Een foto of schermafbeelding bij een ticket (Tom, 15-09-2026). */
export type FeedbackAttachment = {
  id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  author: string;
  /** Gevuld als de foto bij een reactie hoort; leeg = bij het ticket zelf. */
  comment_id: string | null;
  created_at: string;
};

/**
 * Een foto uploaden als ruwe body. De API herkent de soort aan de inhoud en
 * weigert wat geen afbeelding is (415) of groter is dan 10 MB (413).
 */
export const uploadFeedbackAttachment = async (
  ticket: string, bestand: File, commentId?: string,
): Promise<FeedbackAttachment> => {
  const { data } = await api.post<FeedbackAttachment>(
    `/feedback/${encodeURIComponent(ticket)}/attachments`, bestand, {
      headers: { "Content-Type": bestand.type || "application/octet-stream" },
      params: { filename: bestand.name, comment_id: commentId },
    });
  return data;
};

/**
 * De foto zelf, als blob. Een `<img src>` kan geen aanmeldtoken meesturen, dus
 * het scherm haalt hem op en toont hem via een object-URL.
 */
export const fetchFeedbackAttachment = async (ticket: string, id: string): Promise<Blob> => {
  const { data } = await api.get<Blob>(
    `/feedback/${encodeURIComponent(ticket)}/attachments/${id}`, { responseType: "blob" });
  return data;
};
